import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, DialogActions, DialogContent, Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import DropImage from 'components/dropZone/DropImage';
import countries from 'config/countries';
import { VesselModel } from 'models/VesselModel';
import { ModalActionProps } from 'models/AutoModals';


type VesselDialogProps = {
    vessel?: VesselModel,
    action: string,
} & ModalActionProps<VesselModel>

const VesselDialog = ({action, vessel, onFinish}: VesselDialogProps) => {
    const {t} = useTranslation();
    const [signatureFile, setSignatureFile] = useState<File | undefined>();
    const [stampFile, setStampFile] = useState<File | undefined>();
    const [clearSignature, setClearSignature] = useState(0);
    const [clearStamp, setClearStamp] = useState(0);

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const data = Object.fromEntries(new FormData(event.currentTarget).entries()) as unknown as VesselModel;

        onFinish({
            ...vessel,
            ...data,
            captainSignature: clearSignature ? undefined : vessel?.captainSignature,
            captain_signature_file: signatureFile,
            captainSignatureClear: clearSignature,
            captainStamp: clearStamp ? undefined : vessel?.captainStamp,
            captain_stamp_file: stampFile,
            captainStampClear: clearStamp,
        });
    };

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            autoFocus
                            id="name"
                            name="name"
                            label={t('name')}
                            fullWidth
                            required
                            defaultValue={vessel?.name}
                        />

                        <TextField
                            id="flag"
                            name="flag"
                            label={t('flag')}
                            fullWidth
                            required
                            defaultValue={vessel?.flag || ''}
                            select
                        >
                            {Object.entries(countries).map(c => <MenuItem key={c[0]} value={c[0]}>{c[1]}</MenuItem>)}
                        </TextField>

                        <TextField
                            id="capacity"
                            name="capacity"
                            label={t('capacity')}
                            fullWidth
                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                            defaultValue={vessel?.capacity}
                        />

                        <TextField
                            id="space"
                            name="space"
                            label={t('space')}
                            fullWidth
                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                            defaultValue={vessel?.space}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">m<sup>2</sup></InputAdornment>,
                            }}
                        />

                        <TextField
                            id="code"
                            name="code"
                            label={t('code_for_bl')}
                            fullWidth
                            required
                            defaultValue={vessel?.code}
                        />

                        <TextField
                            id="type"
                            name="type"
                            label={t('type')}
                            fullWidth
                            required
                            defaultValue={vessel?.type}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            id="phone"
                            name="phone"
                            label={t('phone')}
                            fullWidth
                            type="tel"
                            defaultValue={vessel?.phone}
                        />

                        <TextField
                            id="email"
                            name="email"
                            label={t('email')}
                            fullWidth
                            type="email"
                            defaultValue={vessel?.email}
                        />

                        <TextField
                            id="captainName"
                            name="captainName"
                            label={t('captain_name')}
                            fullWidth
                            defaultValue={vessel?.captainName}
                        />

                        <TextField
                            id="operator"
                            name="operator"
                            label={t('company')}
                            fullWidth
                            defaultValue={vessel?.operator}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <DropImage
                            upload={setSignatureFile}
                            remove={() => setClearSignature(1)}
                            image={vessel?.captainSignature}
                            title={t('captain_signature')}
                        />

                        <DropImage
                            upload={setStampFile}
                            remove={() => setClearStamp(1)}
                            image={vessel?.captainStamp}
                            title={t('captain_stamp')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button type="submit">{action}</Button>
            </DialogActions>
        </Box>
    );
};

export default VesselDialog;
